import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Autocomplete,
  Paper,
  CircularProgress,
  Grid
} from '@mui/material';
import { Upload, AlertCircle, Edit2, Plus, Building2, CreditCard, Banknote, FileText } from 'lucide-react';
import { API_URL } from '../../../utils/apiConfig';

const UPLOADS_BASE_URL = API_URL ? API_URL.replace(/\/api\/?$/, '') : '';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL

const MAJOR_BANKS = [
  "Bank of America", "Citizens Bank", "JPMorgan Chase", "Wells Fargo",
  "Citibank", "U.S. Bank", "PNC Bank", "Capital One", "TD Bank",
  "BB&T", "SunTrust Bank", "Regions Bank", "Fifth Third Bank",
  "KeyBank", "HSBC Bank", "Huntington Bank", "Santander Bank",
  "M&T Bank", "BMO Harris Bank", "Union Bank",
];

const ALLOWED_MIME_TYPES = {
  'image/jpeg': true,
  'image/png': true,
  'application/pdf': true
};

const DirectDepositForm = ({ 
  employeeId, 
  formId, 
  onSubmit = () => {}, 
  isCompleted = false  
}) => {
  const [formData, setFormData] = useState({
    bankName: '',
    accountNumber: '',
    routingNumber: '',
    accountType: 'Checking',
    bankStatement: null
  });
  const [existingDocument, setExistingDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCompletedState, setIsCompletedState] = useState(isCompleted);
  const [imageUrl, setImageUrl] = useState(null);

  const hasExistingData = formData.bankName || formData.accountNumber || formData.routingNumber;

  useEffect(() => {
    const fetchExistingData = async () => {
      if (!employeeId) {
        setLoading(false);
        return;
      }
  
      console.log('🎰 [DirectDepositForm] Fetching existing data for employee:', employeeId);
      console.log('🎰 [DirectDepositForm] API URL:', `${API_URL}/employees/${employeeId}/direct_deposit`);
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/employees/${employeeId}/direct_deposit`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-store'
          },
        });
  
        console.log('🎰 [DirectDepositForm] Fetch response status:', response.status);
        
        if (!response.ok) {
          if (response.status === 404) {
            console.log('🎰 [DirectDepositForm] No existing data found (404) - opening form for editing');
            // No direct deposit data found - this is normal for new employees
            setIsEditing(true);
            setLoading(false);
            return;
          }
          throw new Error('Failed to fetch direct deposit details');
        }
  
        const data = await response.json();
  
        if (data && (data.bankName || data.accountNumber || data.routingNumber)) {
          setFormData(prev => ({
            ...prev,
            bankName: data.bankName || prev.bankName || '',  // Ensures user selection persists
            accountNumber: data.accountNumber || '',
            routingNumber: data.routingNumber || '',
            accountType: data.accountType || 'Checking'
          }));
  
          if (data.bankStatement) {
            const documentInfo = {
              path: data.bankStatement,
              filename: data.bankStatementOriginalName || data.bankStatement,
              mimeType: data.mimeType || null
            };
            
            setExistingDocument(documentInfo);
          }
        } else {
          setIsEditing(true);
        }
      } catch (err) {
        console.error('❌ [DirectDepositForm] Error fetching direct deposit details:', err);
        console.error('❌ [DirectDepositForm] Error details:', {
          message: err.message,
          stack: err.stack
        });
        // Don't show an error for 404 - just open the form for editing
        if (!err.message?.includes('404')) {
          setError('Failed to load existing direct deposit information');
        }
        setIsEditing(true);
      } finally {
        setLoading(false);
        console.log('🎰 [DirectDepositForm] Initial fetch complete');
      }
    };
  
    fetchExistingData();
  }, [employeeId]);
  

  const validateRoutingNumber = (number) => {
    return /^\d{9}$/.test(number);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const validateFile = (file) => {
    if (!file) return false;
    if (!ALLOWED_MIME_TYPES[file.type]) {
      setError('Invalid file type. Please upload a PDF, JPEG, or PNG file.');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      setFormData(prev => ({ ...prev, bankStatement: file }));
      setError(null);

      if (file.type.startsWith('image/')) {
        const previewUrl = URL.createObjectURL(file);
        setImageUrl(previewUrl);
      } else {
        setImageUrl(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);
  
    // Debugging: Log the form data
    console.log('Form Data:', formData);
  
    if (!validateRoutingNumber(formData.routingNumber)) {
      setError('Please enter a valid 9-digit routing number');
      setSubmitting(false);
      return;
    }
    
    // Always require a document for verification
    if (!formData.bankStatement && !existingDocument) {
      setError('Please upload a bank statement or voided check document to verify your direct deposit information');
      setSubmitting(false);
      return;
    }
  
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
  
      formDataToSend.append('bankName', formData.bankName);
      formDataToSend.append('accountNumber', formData.accountNumber);
      formDataToSend.append('routingNumber', formData.routingNumber);
      formDataToSend.append('accountType', formData.accountType);
  
      if (formData.bankStatement) {
        formDataToSend.append('bankStatement', formData.bankStatement);
      }
  
      const response = await fetch(`${API_URL}/employees/${employeeId}/direct_deposit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formDataToSend
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save direct deposit information');
      }
  
      const updatedData = await response.json();
      
      if (updatedData.bankStatement) {
        // renderDocument() builds the preview <img> from existingDocument, and
        // img-src permits https: — so it survives the /uploads redirect to R2.
        // Pre-fetching the bytes here does not: connect-src covers every redirect
        // hop and the R2 host is not on it, so the fetch threw "Failed to fetch"
        // and reported an already-saved record as a failed save.
        setExistingDocument({
          path: updatedData.bankStatement,
          filename: updatedData.bankStatementOriginalName || updatedData.bankStatement
        });
        setImageUrl(null);
      }
  
      console.log('✅ [DirectDepositForm] Direct deposit saved successfully!');
      setSuccess(true);
      setIsCompletedState(true);
      setIsEditing(false);
      // Pass both formId and filePath (use existing document path if available)
      console.log('💳 [DirectDepositForm] Calling onSubmit callback...');
      onSubmit(formId, existingDocument?.filePath || null);
  
    } catch (err) {
      console.error('❌ [DirectDepositForm] Error saving direct deposit details:', err);
      console.error('❌ [DirectDepositForm] Error details:', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message);
    } finally {
      setSubmitting(false);
      console.log('💳 [DirectDepositForm] Submission process complete');
    }
  };

  const renderDocument = () => {
    if (!existingDocument && !imageUrl) return null;

    const isPdf = existingDocument?.path?.toLowerCase().endsWith('.pdf') || 
                 (formData.bankStatement && formData.bankStatement.type === 'application/pdf');
    const isImage = !isPdf;
    
    const cleanPath = existingDocument?.path?.replace(/^\.\//, '').replace(/^uploads\//, '');
    // Add authentication token to document URL
    const token = localStorage.getItem('token');
    const documentUrl = cleanPath ? `${UPLOADS_BASE_URL}/uploads/${cleanPath}?token=${encodeURIComponent(token)}` : null;
    
    const filename = existingDocument?.filename || 
                    (formData.bankStatement ? formData.bankStatement.name : 'Document');
    
    return (
      <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FileText size={20} style={{ color: '#1976d2' }} />
          <Typography variant="body2" color="text.secondary">
            Bank Statement Document: {filename}
          </Typography>
        </Box>
        
        {isImage && (imageUrl || documentUrl) && (
          <Box sx={{ 
            mt: 2,
            p: 2,
            bgcolor: 'white',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <img
              src={imageUrl || documentUrl}
              alt="Bank Statement"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '4px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                setError('Failed to load document preview');
              }}
            />
          </Box>
        )}
        
        {isPdf && documentUrl && (
          <Box sx={{ 
            mt: 2,
            p: 2,
            bgcolor: 'white',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Box sx={{ 
              width: '100%', 
              height: '300px', 
              mb: 2, 
              border: '1px solid',
              borderColor: 'divider'
            }}>
              {/* inline=1 makes the server stream the PDF from our own origin.
                  Without it the request 302s to an R2 presigned URL, which CSP
                  frame-src blocks, and the employee sees an empty grey box where
                  their bank document should be. The "Open" button below keeps the
                  plain URL — a top-level navigation is not frame-src governed. */}
              <iframe
                src={`${documentUrl}&inline=1#toolbar=1&view=FitH`}
                title="PDF Document"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            </Box>
            
            <Button
              variant="outlined"
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<FileText size={16} />}
            >
              Open PDF
            </Button>
          </Box>
        )}
        
        {isPdf && !documentUrl && formData.bankStatement && (
          <Box sx={{ 
            mt: 2,
            p: 3,
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              PDF preview will be available after submission
            </Typography>
            <Typography variant="body2" fontWeight="500" sx={{ mt: 1 }}>
              {filename}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {(isCompletedState || success) && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
        >
          Direct deposit information has been successfully submitted and verified.
        </Alert>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          icon={<AlertCircle />}
        >
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4, backgroundColor: '#fff', mx: 'auto', margin: 'auto', maxWidth: 600 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Direct Deposit Information
        </Typography>

        {isEditing ? (
        <Box component="form" onSubmit={handleSubmit}>
  <Box sx={{ mb: 3 }}>
  <Typography variant="subtitle2" sx={{ mb: 1 }}>
    Bank Name
  </Typography>
  <Autocomplete
    freeSolo // Allows users to type a custom bank name
    options={MAJOR_BANKS} // List of known banks
    value={formData.bankName}
    onChange={(event, newValue) => {
      setFormData(prev => ({ ...prev, bankName: newValue }));
      setError(null);
    }}
    renderInput={(params) => (
      <TextField 
        {...params} 
        name="bankName"
        onChange={handleInputChange}
        placeholder="Enter or select your bank"
        error={Boolean(error && !formData.bankName)}
        helperText={error && !formData.bankName ? 'Bank name is required' : ''}
        sx={{ bgcolor: 'background.paper' }}
      />
    )}
  />
</Box>


            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Routing Number
              </Typography>
              <TextField
                name="routingNumber"
                value={formData.routingNumber}
                onChange={handleInputChange}
                required
                fullWidth
                error={Boolean(formData.routingNumber && !validateRoutingNumber(formData.routingNumber))}
                helperText={
                  formData.routingNumber && !validateRoutingNumber(formData.routingNumber)
                    ? 'Please enter a valid 9-digit routing number'
                    : ''
                }
                inputProps={{ pattern: "^\\d{9}$" }}
                sx={{ bgcolor: 'background.paper' }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Account Number
              </Typography>
              <TextField
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                required
                fullWidth
                sx={{ bgcolor: 'background.paper' }}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Account Type
              </Typography>
              <TextField
                select
                name="accountType"
                value={formData.accountType}
                onChange={handleInputChange}
                required
                fullWidth
                SelectProps={{ native: true }}
                sx={{ bgcolor: 'background.paper' }}
              >
                <option value="Checking">Checking</option>
                <option value="Savings">Savings</option>
              </TextField>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Bank Statement or Voided Check (Required)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<Upload />}
                sx={{ 
                  width: '100%', 
                  py: 1.5,
                  borderColor: !existingDocument && !formData.bankStatement ? '#d32f2f' : undefined
                }}
              >
                Upload Document
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  required={!existingDocument}
                />
              </Button>
              {formData.bankStatement ? (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Selected: {formData.bankStatement.name}
                </Typography>
              ) : !existingDocument ? (
                <Typography variant="body2" sx={{ mt: 1, color: '#d32f2f' }}>
                  * A bank statement or voided check is required to verify your direct deposit information
                </Typography>
              ) : null}
            </Box>

            {renderDocument()}
          </Box>
        ) : hasExistingData ? (
          <Box>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          bgcolor: 'white', 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <Building2 style={{ color: '#1976d2' }} size={24} />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Bank Name
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="500">
                            {formData.bankName}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          bgcolor: 'white', 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <Banknote style={{ color: '#2e7d32' }} size={24} />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Routing Number
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="500">
                            {formData.routingNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          bgcolor: 'white', 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <CreditCard style={{ color: '#9c27b0' }} size={24} />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Account Number
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="500">
                            {'••••' + formData.accountNumber.slice(-4)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          bgcolor: 'white', 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <FileText style={{ color: '#ed6c02' }} size={24} />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Account Type
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="500">
                            {formData.accountType}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>

            {renderDocument()}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No direct deposit information has been added yet.
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setIsEditing(!isCompleted)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Button
            startIcon={hasExistingData ? <Edit2 /> : <Plus />}
            variant="contained"
            onClick={() => setIsEditing(true)}
            disabled={submitting} // Prevents disabling when completed
          >
            {hasExistingData ? 'Edit Information' : 'Add Direct Deposit'}
          </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

DirectDepositForm.propTypes = {
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSubmit: PropTypes.func,
  isCompleted: PropTypes.bool
};

DirectDepositForm.defaultProps = {
  onSubmit: () => {},
  isCompleted: false
};

export default DirectDepositForm;
